import axios from 'axios';

import { IOrganixInput } from "../../types/IOrganixInput";
import { App } from '../../App';

type SimulationType = 'motorganix' | 'diagenesis';

interface IJobSimulation {
  simulation_id: string;
  status: string;
  created_at: string;
  progress: number;
  depends_on: string[];
  simulation_type: SimulationType;
  download_url?: string;
  // One line from the engine, set when this simulation failed.
  error?: string | null;
}

interface IJob {
  job_id: string;
  status: string;
  progress: number;
  created_at: string;
  simulations: IJobSimulation[];
}

class ForgeApi {

  private static getDatasetId = async (): Promise<{ dataset_id: string, upload_url: string }> => {
    const response = await axios.post<{ dataset_id: string, upload_url: string }>(App.config.forgeURL + "datasets", {});
    return response.data;
  }

  private static upload = async (uploadUrl: string, jsonData: IOrganixInput): Promise<void> => {
    const body = JSON.stringify(jsonData);
    await axios.put(uploadUrl, body, {
      headers: {
        "Content-Type": "application/json"
      }
    });
  }

  private static createJob = async (dataset_id: string, simulations: SimulationType[]): Promise<string> => {
    const res = await axios.post(App.config.forgeURL + "jobs", { dataset_id, simulations });
    return res.data.job_id;
  }

  /**
   * Upload a dataset and start a job with the specified simulations.
   * @param input The motorganix input data
   * @param simulations Which simulations to run, e.g. ["motorganix"] or ["motorganix", "diagenesis"]
   * @returns Job ID, or null on error
   */
  static start = async (input: IOrganixInput, simulations: SimulationType[]): Promise<string | null> => {
    // Which of the three calls failed, for the log. A CORS refusal on the S3
    // upload looks like a network error with no status.
    let step = "POST datasets";
    try {
      const { dataset_id, upload_url } = await this.getDatasetId();
      step = "PUT dataset to S3";
      await this.upload(upload_url, input);
      step = "POST jobs";
      const job_id = await this.createJob(dataset_id, simulations);
      return job_id;
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      console.error(`Forge job failed to start at ${step}${status ? ` (HTTP ${status})` : " (no response; network or CORS)"}:`,
        e instanceof Error ? e.message : String(e));
      return null;
    }
  }

  /**
   * Retrieve job status and progress.
   * @param job_id Unique job ID
   */
  static getJob = async (job_id: string): Promise<IJob> => {
    const res = await axios.get<IJob>(App.config.forgeURL + `jobs/${job_id}`);
    return res.data;
  }

  /**
   * Cancel a job. Forge stops the job's running AWS task, so it stops costing
   * compute time. Best effort: errors are logged, not thrown, since the caller
   * has already abandoned the job.
   * @param job_id Unique job ID
   */
  static cancel = async (job_id: string): Promise<void> => {
    try {
      await axios.delete(App.config.forgeURL + `jobs/${job_id}`);
    } catch (e) {
      console.warn(`Failed to cancel Forge job ${job_id}:`, e instanceof Error ? e.message : String(e));
    }
  }

  /**
   * Retrieve a specific simulation.
   * @param simulation_id Unique simulation ID
   */
  static getSimulation = async (simulation_id: string): Promise<IJobSimulation> => {
    const res = await axios.get<IJobSimulation>(App.config.forgeURL + `simulations/${simulation_id}`);
    return res.data;
  }

  /**
   * List all simulations.
   */
  static listSimulations = async (): Promise<IJobSimulation[]> => {
    const res = await axios.get<IJobSimulation[]>(App.config.forgeURL + "simulations");
    return res.data;
  }
}

export { ForgeApi, SimulationType, IJob, IJobSimulation }
